import type { ReactNode } from 'react';
import type { TableColumn } from '@interstate/components/Table';
import type { BadgeVariant } from '@interstate/components/Badge';

export const CHUNK_SIZE = 5; // Number of items to display per chunk
export const BANNER_WARNING_DELAY = 5000; // 5 seconds
export const loadingDesc = 'Loading Deal Versions...';
export type BannerType = 'success' | 'warning' | 'error';

export interface MessageBannerConfig {
    title: string;
    description: string;
    alertSeverity: 'success' | 'caution' | 'error';
}
// Define messages for different banner types
export const messages: Record<BannerType, MessageBannerConfig> = {
    success: {
        title: 'All Records Loaded!',
        description: 'All deal records are available.',
        alertSeverity: 'success'
    },
    warning: {
        title: 'Multiple Revisions Detected',
        description: 'Multiple revisions of some records have been made that may impact this deal record.',
        alertSeverity: 'caution'
    },
    error: {
        title: 'Print Error',
        description: 'Oops! Something went wrong. Please try again.',
        alertSeverity: 'error'
    }
};

export interface BadgeProps {
    children: React.ReactNode;
    variant: BadgeVariant;
}

export interface InfoCardField {
    key: string; // Property name in the data object
    label: string; // Display label for the field
    render?: (value: any, data?: any) => ReactNode; // Optional custom renderer
    unit?: string; // Optional unit for the field, e.g., 'USD', '%',
    labelWidth?: string; // Optional width for the label, e.g., '45px'
}

// Row/group configuration
export interface InfoCardRow {
    fields: InfoCardField[];
}

// Main component props
export interface InfoCardProps {
    title?: string;
    data: any;
    rows: InfoCardRow[];
    testId?: string;
}

export type ModifiedBy = {
    offerSource?: string;
    applicationName?: string;
};

export const DealInformationColumn: InfoCardRow[] = [
    {
        fields: [
            { key: 'model', label: 'Vehicle', labelWidth: '45px' },
            { key: 'stockNumber', label: 'Stock', labelWidth: '45px' },
            { key: 'vin', label: 'Vin#', labelWidth: '45px' }
        ]
    },
    {
        fields: [
            { key: 'inventoryType', label: 'Type', labelWidth: '59px' },
            { key: 'dealType', label: 'Deal Type', labelWidth: '59px' },
            { key: 'term', label: 'Term', unit: 'months', labelWidth: '59px' }
        ]
    },
    {
        fields: [
            { key: 'totalAllowance', label: 'Trade', unit: '$', labelWidth: '74px' },
            { key: 'monthlyPayment', label: 'Payment', unit: '$', labelWidth: '74px' },
            { key: 'sellingPrice', label: 'Selling Price', unit: '$', labelWidth: '74px' }
        ]
    },
    {
        fields: [
            { key: 'downPayment', label: 'Down', unit: '$', labelWidth: '36px' },
            { key: 'frontEndProfit', label: 'Front', unit: '$', labelWidth: '36px' },
            { key: 'backEndProfit', label: 'Back', unit: '$', labelWidth: '36px' }
        ]
    }
];

export const DealInformationColumnDefs: TableColumn[] = [
    {
        dataIndex: 'inventoryType',
        title: 'Type',
        width: 120
    },
    {
        dataIndex: 'dealType',
        title: 'Deal Type',
        width: 120
    },
    {
        dataIndex: 'term',
        title: 'Term',
        width: 120
    },
    {
        dataIndex: 'downPayment',
        title: 'Down',
        width: 120
    },
    {
        dataIndex: 'frontEndProfit',
        title: 'Front',
        width: 120
    },
    {
        dataIndex: 'backEndProfit',
        title: 'Back',
        width: 120
    }
];
export interface IAuditLogActivity {
    baseRoute: string;
}
